import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  FillStyles,
  IncidentStyleMap,
  incidentTypes,
  StyleMap,
} from "../types";
import { ExpressionSpecification } from "mapbox-gl";
import { globalStyles } from "../../../../../../styles/globalStyleTheming";

const { palette } = globalStyles;
export const incidentMarkerMinimumPixelSize = 28;
export const incidentMarkerLayerId = "incident-marker-layer";

const incidentStyleMapper = {
  FIRE: {
    id: "fire",
    colour: palette.brand.sun,
    opacity: 0.4,
    background: "#fbefec",
  },
  DEFORESTATION: {
    id: "deforestation",
    colour: palette.incident.deforestation,
    opacity: 0.4,
    background: "#edf9ed",
  },
  DROUGHT: {
    id: "drought",
    colour: palette.brand.sand,
    opacity: 0.4,
    background: "#faf8ef",
  },
  FLOOD: {
    id: "flood",
    colour: palette.brand.sea,
    opacity: 0.4,
    background: "#eef3fb",
  },
  EARTHQUAKE: {
    id: "earthquake",
    colour: palette.incident.earthquake,
    opacity: 0.4,
    background: "#f6eeeb",
  },
  "TROPICAL CYCLONE": {
    id: "tropical-cyclone",
    colour: palette.brand.sky,
    opacity: 0.4,
    background: "#f1fafb",
  },
  VOLCANO: {
    id: "volcano",
    colour: palette.brand.carbon,
    opacity: 0.4,
    background: "#f4f4f4",
  },
  TSUNAMI: {
    id: "tsunami",
    colour: palette.incident.tsunami,
    opacity: 0.4,
    background: "#fafafa",
  },
} satisfies IncidentStyleMap;

export const styleMapper = {
  outline: {
    id: "outline",
    colour: palette.brand.earthly,
    opacity: 0.4,
    background: "#effcf7",
  },
  buffer: {
    id: "buffer",
    colour: palette.brand.earthly,
    opacity: 0.2,
    background: "#effcf7",
  },
  FallbackOption: {
    id: "fallback",
    colour: palette.brand.plain,
    opacity: 0.4,
    background: "#fafafa",
  },
  ...incidentStyleMapper,
} satisfies StyleMap;

export const createFillAppearance = (style: FillStyles) => {
  const { id, colour, opacity } = style;

  return {
    id: `${id}-fill`,
    type: "fill" as const,
    paint: {
      "fill-color": colour,
      "fill-opacity": opacity,
    },
  };
};

export const createLineAppearance = (
  id: string,
  colour: string,
  width: number,
) => ({
  id,
  type: "line" as const,
  paint: {
    "line-color": colour,
    "line-width": width,
  },
});

export const Title = styled(Typography)({
  fontSize: "13px",
  fontWeight: 700,
  paddingTop: 4,
});

export const interactiveLayerIds = [
  `${styleMapper.buffer.id}-fill`,
  `${styleMapper.outline.id}-fill`,
  ...incidentTypes.map(
    (incidentType) => `${styleMapper[incidentType].id}-fill`,
  ),
  incidentMarkerLayerId,
];

export const createIncidentMarkerColour = (): ExpressionSpecification => [
  "match",
  ["get", "incident"],
  ...incidentTypes.flatMap((incidentType) => [
    incidentType,
    styleMapper[incidentType].colour,
  ]),
  palette.brand.earthly,
];
